import { useState } from "react";
import { CheckCircle2, Loader2Icon } from "lucide-react";
import {
  Button,
  DatePicker,
  DialogFooter,
  Input,
  ToggleGroup,
  ToggleGroupItem,
  V2Label,
  useMultiStepForm,
} from "web-design-system";
import {
  useMinorGuardianKycCheck,
  useUpdateInvestorProfileDetails,
} from "creso-core/hooks";
import {
  INCOME_RANGE,
  OCCUPATION,
  PEP_STATUS,
  SOURCE_OF_WEALTH,
} from "creso-core/constants";
import { panSchema } from "creso-core/zod";
import type { InvestorProfileDetails } from "creso-core/types";
import { format, parseISO } from "date-fns";
import { handleError } from "../../utils";
import { useClientOnboarding } from "./context";
import {
  type GuardianRelationship,
  useMinorOnboarding,
} from "./minor-form-data";

const RELATIONSHIPS: { label: string; value: GuardianRelationship }[] = [
  { label: "Father", value: "father" },
  { label: "Mother", value: "mother" },
  { label: "Court appointed", value: "court_appointed_legal_guardian" },
];

const labelOf = (
  options: { label: string; value: string }[],
  value?: string | null
) => (value ? (options.find((o) => o.value === value)?.label ?? value) : "—");

const TOGGLE_ITEM_CLASS =
  "px-3 py-2 border-[1.5px] text-primary font-normal rounded-lg data-[state=on]:border-primary data-[state=on]:font-semibold";

// Only a guardian who is your client has a profile to show
function GuardianProfileRows({
  pan,
  profile,
}: {
  pan: string;
  profile: InvestorProfileDetails | null;
}) {
  const rows = [["PAN", pan], ...(profile ? profileRows(profile) : [])];

  return (
    <div className="flex flex-col text-sm">
      {rows.map(([label, value]) => (
        <div
          key={label}
          className="flex justify-between gap-4 border-t border-border px-4 py-3"
        >
          <span className="text-muted-foreground">{label}</span>
          <span className="font-medium">{value}</span>
        </div>
      ))}
    </div>
  );
}

function profileRows(profile: InvestorProfileDetails) {
  return [
    [
      "Date of birth",
      profile?.date_of_birth
        ? format(parseISO(profile.date_of_birth), "dd/MM/yyyy")
        : "—",
    ],
    ["Occupation", labelOf(OCCUPATION, profile?.occupation)],
    ["Income range", labelOf(INCOME_RANGE, profile?.income_slab)],
    ["Source of wealth", labelOf(SOURCE_OF_WEALTH, profile?.source_of_wealth)],
    ["Political exposure", labelOf(PEP_STATUS, profile?.pep_details)],
  ];
}

/** Minor onboarding step 2: who the guardian is (read-only once found) */
export function Guardian() {
  const { client } = useClientOnboarding();
  const { previousStep, nextStep } = useMultiStepForm();
  const { guardian: savedGuardian } = useMinorOnboarding();
  const [relationship, setRelationship] = useState<GuardianRelationship | "">(
    savedGuardian?.relationship ?? ""
  );
  const [pan, setPan] = useState(savedGuardian?.pan ?? "");
  const [dateOfBirth, setDateOfBirth] = useState(
    savedGuardian?.profile.date_of_birth ?? ""
  );
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { check, isChecking, result } = useMinorGuardianKycCheck(client);
  const { mutateAsync: updateProfile } = useUpdateInvestorProfileDetails();

  // The linked guardian counts as checked; a new PAN needs a KYC check, which
  // also links it in place of the old one
  const linked = savedGuardian?.pan === pan ? savedGuardian : null;
  const checked = linked
    ? {
        pan,
        name: linked.name,
        kycStatus: linked.profile.kyc_status,
        readOnly: linked.readOnly,
      }
    : result?.pan === pan
      ? {
          pan,
          name: result.name,
          kycStatus: result.kycStatus,
          readOnly: !!result.guardianClient,
        }
      : null;
  const isVerified = checked?.kycStatus === "S";
  // A guardian who isn't your client yet also needs their date of birth
  const isNew = isVerified && !checked.readOnly;
  const canContinue =
    isVerified && !!linked && !!relationship && (!isNew || !!dateOfBirth);

  const handleCheck = async () => {
    const parsed = panSchema.safeParse(pan);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Invalid PAN");
      return;
    }
    setError(null);
    try {
      // A linked guardian is replaced through the profiles API
      await check({ guardianPan: pan, replace: !!savedGuardian });
      // Linking a new guardian clears the relationship
      setRelationship("");
    } catch (e) {
      handleError(e, "Failed to check KYC status");
    }
  };

  const handleNext = async () => {
    if (!canContinue || !client) return;
    try {
      setIsSaving(true);
      await updateProfile({
        client_id: client.id,
        holder_role: "guardian",
        relationship,
        // A guardian who is your client can only have the relationship set
        ...(isNew ? { date_of_birth: dateOfBirth } : {}),
      });
      nextStep();
    } catch (e) {
      handleError(e, "Failed to save guardian");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col size-full p-6 gap-6">
      <span className="text-lg font-semibold">Guardian</span>
      {/* Padding keeps focus rings from being clipped by the scroll area */}
      <div className="flex flex-col flex-1 gap-5 overflow-y-auto -m-1 p-1">
        <div className="flex flex-col gap-1.5">
          <V2Label>
            {checked?.name
              ? `${checked.name.split(" ")[0]} is the minor's`
              : "Relationship to the minor"}
          </V2Label>
          <ToggleGroup
            type="single"
            value={relationship}
            onValueChange={(value) =>
              value && setRelationship(value as GuardianRelationship)
            }
            className="w-fit gap-2 flex-wrap justify-start bg-background"
          >
            {RELATIONSHIPS.map((option) => (
              <ToggleGroupItem
                key={option.value}
                value={option.value}
                variant="outline"
                className={TOGGLE_ITEM_CLASS}
              >
                {option.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          {relationship === "court_appointed_legal_guardian" && (
            <span className="text-xs text-destructive">
              Court order needed in Documents
            </span>
          )}
        </div>

        {/* Once linked, the guardian card shows the PAN */}
        {!(linked && isVerified) && (
          <div className="flex flex-col gap-1.5">
            <V2Label>Guardian PAN</V2Label>
            <div className="flex flex-row items-start gap-2">
              <Input
                value={pan}
                onChange={(e) => {
                  setPan(e.target.value.toLocaleUpperCase());
                  setError(null);
                }}
                disabled={isChecking || isSaving}
                maxLength={10}
                placeholder="ABCDE1234F"
                className="flex-1"
              />
              {!checked && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={!pan || isChecking}
                  onClick={handleCheck}
                >
                  {isChecking && (
                    <Loader2Icon className="size-4 animate-spin" />
                  )}
                  Check KYC
                </Button>
              )}
            </div>
            {!!error && (
              <span className="text-xs text-destructive">{error}</span>
            )}
            {checked && !isVerified && (
              <div className="rounded-lg p-3 text-xs bg-tag-orange text-tag-orange-foreground border border-tag-orange-foreground/30">
                No KYC found for this PAN
              </div>
            )}
          </div>
        )}

        {isVerified && checked && (
          <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-secondary">
            <div className="flex items-start justify-between gap-2 p-4">
              <span className="text-sm font-semibold">{checked.name}</span>
              <span className="flex items-center gap-1 text-xs font-medium text-constructive">
                <CheckCircle2 className="size-3.5" />
                KYC Verified
              </span>
            </div>
            <GuardianProfileRows
              pan={checked.pan}
              profile={checked.readOnly ? (linked?.profile ?? null) : null}
            />
          </div>
        )}

        {isNew && (
          <div className="flex flex-col gap-1.5">
            <V2Label>Guardian&apos;s date of birth</V2Label>
            <DatePicker
              placeholder="DD/MM/YYYY"
              value={dateOfBirth}
              onChange={setDateOfBirth}
              disabled={(date) => date > new Date()}
            />
          </div>
        )}
      </div>
      <DialogFooter className="flex sm:flex-col gap-4">
        <div className="flex items-center w-full gap-5">
          <Button type="button" variant="outline" onClick={previousStep}>
            Previous
          </Button>
          <Button
            className="flex-1"
            type="button"
            disabled={!canContinue || isSaving}
            onClick={handleNext}
          >
            {isSaving && <Loader2Icon className="size-4 animate-spin" />}
            Next
          </Button>
        </div>
      </DialogFooter>
    </div>
  );
}
